<script setup lang="ts">
import type { SearchInput, SearchResultFragment } from '#graphql-operations'
import { isDeepEqual } from '@antfu/utils'

const props = defineProps({
  search: {
    type: Object as PropType<SearchInput>,
    required: true,
  },
  bannerImage: String,
  reverse: Boolean,
  bgColor: String,
  resetSearchQueryParams: {
    type: Array as PropType<string[]>,
    default: undefined,
  },
})

const emit = defineEmits<{
  (event: 'total', value: number): void
  (event: 'loading', value: boolean): void
}>()

const { t } = useI18n()
const productHistoryStore = useProductHistoryStore()
const { ecommerce } = useAnalytics()
const collectionsStore = useCollectionsStore()
const slots = useSlots()
const isLoading = ref(true)
const isFetchingData = ref(false)
const total = ref(0)
const take = computed(() => props.search?.take || 8)

// Get items directly from useFetchSearch
const { items, fetchSearch, clearFetchResults } = useFetchSearch({
  take,
  total,
  isFetchingData,
  isLoading,
  resetSearch: resetPage,
  resetSearchQueryParams: props.resetSearchQueryParams,
})

function resetPage() {
  isLoading.value = true
  clearFetchResults()
  fetchInitialProducts()
}

async function fetchInitialProducts() {
  await fetchSearch({
    page: 1,
    search: props.search,
  })
  isLoading.value = false
}

watch(total, () => {
  emit('total', total.value)
})

watchEffect(() => {
  emit('loading', isLoading.value)
})

watch(() => props.search, (newSearch, oldSearch) => {
  if (newSearch === undefined || oldSearch === undefined) {
    return
  }
  if (!isDeepEqual(newSearch, oldSearch)) {
    resetPage()
  }
}, { deep: true })

onBeforeMount(() => {
  fetchInitialProducts()
})

function handleClick(item: SearchResultFragment) {
  productHistoryStore.addItemToHistory(item)
  ecommerce.selectItem(item)
}
</script>

<template>
  <div class="relative grid grid-cols-2 gap-y-4 pb4 lg:grid-cols-6 lg:gap-x-2 lg:pb4 md:grid-cols-2 sm:grid-cols-2">
    <div
      class="relative max-h-182 min-h-40 items-center overflow-hidden rd-md px4 pt4"
      :class="[
        reverse
          ? 'col-span-2 lg:col-span-2 sm:col-span-2 md:col-span-2 order-first'
          : 'col-span-2 lg:col-span-2 sm:col-span-2 md:col-span-2 order-last',
        collectionsStore.rootCollections.status === 'pending' ? 'n-skeleton' : bgColor,
      ]"
    >
      <div v-if="collectionsStore.rootCollections.status !== 'pending'" class="min-h-40 flex justify-between lg:min-h-unset lg:flex-col">
        <div class="flex flex-col items-center justify-center gap-y-2">
          <slot />
        </div>
        <div class="lg:absolute lg:bottom-0 lg:left-0 lg:right-0 lg:h-80% lg:overflow-hidden">
          <NuxtImg
            :src="bannerImage"
            alt="Banner"
            class="aspect-ratio h-full object-contain"
            width="357"
            height="536"
            preload
          />
        </div>
      </div>
    </div>

    <div
      class="relative items-center overflow-hidden"
      :class="[
        reverse
          ? 'col-span-3 lg:col-span-4 sm:col-span-2 md:col-span-2 order-last'
          : 'col-span-3 lg:col-span-4 sm:col-span-2 md:col-span-2 order-first',
      ]"
    >
      <div class="flex-grow -mt6">
        <div
          v-if="total !== 0 && !isLoading"
          class="grid grid-cols-2 mt6 gap-x-2 gap-y-4 sm:grid-cols-minmax-12rem"
          data-testid="collectionsProductGridCTA"
        >
          <div
            v-for="(entity, index) in items"
            :key="`${entity.id}-${index}`"
            :data-testid="index"
          >
            <LazyProductCard
              :item="entity"
              type="product"
              :data-testid="`productGridItem-${entity.id}`"
              @click="handleClick(entity)"
            />
          </div>
        </div>

        <!-- skeleton on loading -->
        <div
          v-if="isLoading"
          class="grid grid-cols-2 mt6 gap-x-2 gap-y-4 sm:grid-cols-minmax-12rem"
        >
          <LazyProductCardSkeleton
            v-for="n in take"
            :key="n"
          />
        </div>

        <template v-if="total === 0 && !isLoading">
          <slot v-if="slots['empty-result']" name="empty-result" />
          <div v-else>
            {{ t('search.no_results_found') }}
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
